(() => {
  if (customElements.get('mlfa-lore-audio')) return;
  let activePlayer = null;
  const clock = (seconds) => {
    const value = Math.max(0, Math.floor(Number(seconds) || 0));
    return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
  };
  class LoreAudio extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.audio = this.querySelector('audio');
      this.playButton = this.querySelector('[data-audio-play]');
      this.seek = this.querySelector('[data-audio-seek]');
      this.rewind = this.querySelector('[data-audio-rewind]');
      this.status = this.querySelector('[data-audio-status]');
      this.elapsed = this.querySelector('[data-audio-elapsed]');
      this.durationLabel = this.querySelector('[data-audio-duration]');
      this.speed = this.querySelector('[data-audio-speed]');
      this.querySelector('.lore-audio-controls').hidden = false;
      this.classList.add('enhanced');
      this.playButton.addEventListener('click', () => this.toggle());
      this.seek.addEventListener('input', () => {
        if (Number.isFinite(this.audio.duration)) {
          this.audio.currentTime = Number(this.seek.value);
          this.updateProgress();
        }
      });
      this.rewind.addEventListener('click', () => {
        this.audio.currentTime = Math.max(0, this.audio.currentTime - 10);
        this.updateProgress();
      });
      this.speed.addEventListener('change', () => {
        this.audio.playbackRate = Number(this.speed.value);
      });
      this.audio.addEventListener('loadedmetadata', () => {
        if (!Number.isFinite(this.audio.duration)) return;
        this.seek.max = this.audio.duration;
        this.seek.disabled = false;
        this.rewind.disabled = false;
        this.durationLabel.textContent = clock(this.audio.duration);
        this.updateProgress();
      });
      this.audio.addEventListener('timeupdate', () => this.updateProgress());
      this.audio.addEventListener('play', () => {
        if (activePlayer !== this) { this.audio.pause(); return; }
        this.paint(true, this.dataset.loading);
      });
      this.audio.addEventListener('playing', () => this.paint(true, this.dataset.listening));
      this.audio.addEventListener('waiting', () => {
        if (this.wantsPlay) this.paint(true, this.dataset.loading);
      });
      this.audio.addEventListener('pause', () => {
        if (this.wantsPlay) {
          this.wantsPlay = false;
          this.paint(false, this.dataset.paused);
        }
      });
      this.audio.addEventListener('ended', () => {
        this.wantsPlay = false;
        this.paint(false, this.dataset.complete);
        if (activePlayer === this) activePlayer = null;
      });
      this.audio.addEventListener('error', () => this.fail());
      this.updateProgress();
    }
    disconnectedCallback() {
      this.stop();
      if (activePlayer === this) activePlayer = null;
    }
    paint(playing, status) {
      this.toggleAttribute('data-active', playing);
      this.playButton.setAttribute('aria-label', playing ? this.dataset.pause : this.dataset.play);
      this.status.textContent = status;
    }
    stop() {
      this.wantsPlay = false;
      this.audio?.pause();
      if (this.playButton) this.paint(false, this.dataset.paused);
    }
    fail() {
      this.wantsPlay = false;
      this.audio.pause();
      this.paint(false, this.dataset.error);
      if (activePlayer === this) activePlayer = null;
    }
    async toggle() {
      if (this.wantsPlay || !this.audio.paused) {
        this.stop();
        if (activePlayer === this) activePlayer = null;
        return;
      }
      if (activePlayer && activePlayer !== this) activePlayer.stop();
      activePlayer = this;
      this.wantsPlay = true;
      this.paint(true, this.dataset.loading);
      if (this.audio.error) this.audio.load();
      if (this.audio.ended) this.audio.currentTime = 0;
      try {
        await this.audio.play();
        if (!this.wantsPlay || activePlayer !== this) this.audio.pause();
      } catch (error) {
        if (error.name !== 'AbortError' && this.wantsPlay) this.fail();
      }
    }
    updateProgress() {
      const duration = Number.isFinite(this.audio.duration) ? this.audio.duration : Number(this.dataset.duration);
      const current = this.audio.currentTime || 0;
      this.seek.value = current;
      this.seek.style.setProperty('--played', `${duration ? Math.min(100, current / duration * 100) : 0}%`);
      this.seek.setAttribute('aria-valuetext', `${clock(current)} / ${clock(duration)}`);
      this.elapsed.textContent = clock(current);
    }
  }
  customElements.define('mlfa-lore-audio', LoreAudio);
  window.addEventListener('pagehide', () => { activePlayer?.stop(); activePlayer = null; });
})();
